import { type QuoteFilters } from '../../../models/quote-filters';
import { type Quote } from '../../../models/quote.model';
import { toIsoUtc } from '../quote-helpers';

// Sentinel for the "Unassigned" inside rep option. Real rep filters match on
// name, so an empty-string value would be indistinguishable from "no filter".
export const UNASSIGNED_INSIDE_REP = '__unassigned__';

export function createEmptyQuoteFilters(): QuoteFilters {
	return {
		search: '',
		nsCustomer: '',
		insideRep: '',
		nsStatus: '',
		quoteStatus: '',
		startDate: '',
		endDate: '',
		dueStartDate: '',
		dueEndDate: ''
	};
}

// Inside rep names are stored "Last, First" (see the Inside Rep filter dropdown
// options), but the search box is typed as "First Last", so a plain substring
// match never hits. Matching word-by-word regardless of order handles both.
function matchesRepName(name: string, search: string): boolean {
	const words = (s: string): string[] =>
		s.toLowerCase().replace(/,/g, ' ').split(/\s+/).filter(Boolean);
	const nameWords = words(name);
	return words(search).every((w) => nameWords.some((nw) => nw.includes(w)));
}

export function filterQuotes(quotes: Quote[], filters: QuoteFilters): Quote[] {
	let filtered = [...quotes];

	const search = filters.search.toLowerCase().trim();
	if (search) {
		filtered = filtered.filter(
			(q) =>
				q.customer.toLowerCase().includes(search) ||
				q.emailSummary.toLowerCase().includes(search) ||
				q.outsideRep.name.toLowerCase().includes(search) ||
				(!!q.insideRep && matchesRepName(q.insideRep, search))
		);
	}

	if (filters.nsCustomer) {
		const nsCustomer = filters.nsCustomer.toLowerCase();
		filtered = filtered.filter(
			(q) => (q.nsCustomerName ?? '').toLowerCase() === nsCustomer
		);
	}

	if (filters.insideRep) {
		const insideRep = filters.insideRep;
		filtered =
			insideRep === UNASSIGNED_INSIDE_REP
				? filtered.filter((q) => !q.insideRep)
				: filtered.filter(
						(q) =>
							(q.insideRep ?? '').toLowerCase() ===
							insideRep.toLowerCase()
					);
	}

	if (filters.nsStatus) {
		const nsStatus = filters.nsStatus.toLowerCase();
		filtered = filtered.filter(
			(q) => String(q.nsEstimateStatus).toLowerCase() === nsStatus
		);
	}

	if (filters.quoteStatus) {
		const quoteStatus = filters.quoteStatus.toLowerCase();
		filtered = filtered.filter(
			(q) => String(q.nsQuoteStatus).toLowerCase() === quoteStatus
		);
	}

	if (filters.startDate) {
		const start = new Date(filters.startDate);
		filtered = filtered.filter(
			(q) => new Date(toIsoUtc(q.receivedDate)) >= start
		);
	}

	if (filters.endDate) {
		const end = new Date(filters.endDate);
		filtered = filtered.filter(
			(q) => new Date(toIsoUtc(q.receivedDate)) <= end
		);
	}

	if (filters.dueStartDate) {
		const dueStart = new Date(filters.dueStartDate);
		filtered = filtered.filter(
			(q) => !!q.dueDate && new Date(toIsoUtc(q.dueDate)) >= dueStart
		);
	}

	if (filters.dueEndDate) {
		const dueEnd = new Date(filters.dueEndDate);
		filtered = filtered.filter(
			(q) => !!q.dueDate && new Date(toIsoUtc(q.dueDate)) <= dueEnd
		);
	}

	return filtered;
}
